import * as React from "react"
import { useTranslation } from "react-i18next"
import * as Primitives from "@radix-ui/react-popover"
import { TimeValue } from "@react-aria/datepicker"
import { Time } from "@internationalized/date"
import { type Locale } from "date-fns"
import { format } from "~/utils/date-utils"
import { Calendar as CalendarPrimitive } from "./calendar"
import { clx } from "../../utils/clx"
import { Button, CalendarIcon, MinusMiniIcon } from "~/design-system"
import { inputBaseStyles } from "~/design-system/components/input"
import { TimeInput } from "./time-input"
import { cva } from "cva"

const isBrowserLocaleClockType24h = () => {
  const language =
    typeof window !== "undefined" ? window.navigator.language : "en-US"

  const hr = new Intl.DateTimeFormat(language, {
    hour: "numeric",
  }).format()

  return Number.isInteger(Number(hr))
}

type DateRange = {
  /**
   * The range's start date.
   */
  from: Date | undefined
  /**
   * The range's end date.
   */
  to?: Date | undefined
}

interface DisplayProps extends React.ComponentProps<"button"> {
  placeholder?: string
}

const Display = React.forwardRef<HTMLButtonElement, DisplayProps>(
  (
    {
      className,
      children,
      /**
       * Placeholder of the date picker's input.
       */
      placeholder,
      ...props
    }: DisplayProps,
    ref
  ) => {
    return (
      <Primitives.Trigger asChild>
        <button
          ref={ref}
          className={clx(
            inputBaseStyles,
            "flex items-center gap-x-2 txt-base-regular h-10 px-3 py-2",
            "data-[state=open]:shadow-borders-interactive-with-active",
            className,
          )}
          {...props}
        >
          <CalendarIcon className="text-ui-fg-muted" />
          <span className="flex-1 truncate text-left">
            {children ? (
              children
            ) : placeholder ? (
              <span className="text-ui-fg-muted">{placeholder}</span>
            ) : null}
          </span>
        </button>
      </Primitives.Trigger>
    )
  }
)
Display.displayName = "DatePicker.Display"

const Flyout = React.forwardRef<
  React.ElementRef<typeof Primitives.Content>,
  React.ComponentProps<typeof Primitives.Content> & { disablePortal?: boolean }
>(({ className, children, disablePortal, ...props }, ref) => {
  const content = (
    <Primitives.Content
      ref={ref}
      sideOffset={8}
      align="center"
      className={clx(
        "txt-compact-small shadow-elevation-flyout bg-ui-bg-base w-fit rounded-lg z-60",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    >
      {children}
    </Primitives.Content>
  )

  return disablePortal ? content : (
    <Primitives.Portal>
      {content}
    </Primitives.Portal>
  )
})
Flyout.displayName = "DatePicker.Flyout"

const formatDate = (date: Date, includeTime?: boolean) => {
  const usesAmPm = !isBrowserLocaleClockType24h()

  if (includeTime) {
    if (usesAmPm) {
      return format(date, "MMM d, yyyy h:mm a")
    }

    return format(date, "MMM d, yyyy HH:mm")
  }

  return format(date, "MMM d, yyyy")
}

type CalendarProps = {
  /**
   * The year to start showing the dates from.
   */
  fromYear?: number
  /**
   * The year to show dates to.
   */
  toYear?: number
  /**
   * The month to start showing dates from.
   */
  fromMonth?: Date
  /**
   * The month to show dates to.
   */
  toMonth?: Date
  /**
   * The day to start showing dates from.
   */
  fromDay?: Date
  /**
   * The day to show dates to.
   */
  toDay?: Date
  /**
   * The date to show dates from.
   */
  fromDate?: Date
  /**
   * The date to show dates to.
   */
  toDate?: Date
  /**
   * Locale to use for formatting dates. To change the locale pass a `date-fns` locale object.
   */
  locale?: Locale
}

interface PickerProps extends CalendarProps {
  /**
   * The class name to apply on the date picker.
   */
  className?: string
  /**
   * Whether the date picker's input is disabled.
   */
  disabled?: boolean
  /**
   * Whether the date picker's input is required.
   */
  required?: boolean
  /**
   * The date picker's placeholder.
   */
  placeholder?: string
  /**
   * Whether to show a time picker along with the date picker.
   */
  showTimePicker?: boolean
  /**
   * Whether to disable the portal. Useful when rendering
   * within nested portal contexts like modals...etc.
   */
  disablePortal?: boolean

  id?: string
  "aria-invalid"?: boolean
  "aria-label"?: string
  "aria-labelledby"?: string
  "aria-required"?: boolean
}

interface SingleProps extends PickerProps {
  defaultValue?: Date
  value?: Date
  onChange?: (date: Date | undefined) => void
}

const SingleDatePicker = ({
  defaultValue,
  value,
  onChange,
  showTimePicker,
  disabled,
  className,
  placeholder,
  disablePortal,
  ...props
}: SingleProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [date, setDate] = React.useState<Date | undefined>(
    value ?? defaultValue ?? undefined
  )
  const [month, setMonth] = React.useState<Date | undefined>(date)

  const [time, setTime] = React.useState<TimeValue>(
    value
      ? new Time(value.getHours(), value.getMinutes())
      : defaultValue
      ? new Time(defaultValue.getHours(), defaultValue.getMinutes())
      : new Time(0, 0)
  )

  const initialDate = React.useMemo(() => {
    return date
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * Update the date when the value changes.
   */
  React.useEffect(() => {
    setDate(value ?? defaultValue ?? undefined)
  }, [value, defaultValue])

  React.useEffect(() => {
    if (date) {
      setMonth(date)
    }
  }, [date])

  React.useEffect(() => {
    if (!open) {
      setMonth(date)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onCancel = () => {
    setDate(initialDate)
    setTime(
      initialDate
        ? new Time(initialDate.getHours(), initialDate.getMinutes())
        : new Time(0, 0)
    )
    setOpen(false)
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onCancel()
    }

    setOpen(open)
  }

  const onDateChange = (date: Date | undefined) => {
    const newDate = date

    if (showTimePicker) {
      /**
       * If the time is cleared, and the date is
       * changed then we want to reset the time.
       */
      if (newDate && !time) {
        setTime(new Time(0, 0))
      }

      /**
       * If the time is set, and the date is changed
       * then we want to update the date with the
       * time.
       */
      if (newDate && time) {
        newDate.setHours(time.hour)
        newDate.setMinutes(time.minute)
      }
    }

    setDate(newDate)
  }

  const onTimeChange = (time: TimeValue) => {
    setTime(time)

    if (!date) {
      return
    }

    const newDate = new Date(date.getTime())

    if (!time) {
      /**
       * When a segment of the time input is cleared,
       * it will return `null` as the value is no longer
       * a valid time. In this case, we want to set the
       * time to for the date, effectivly resetting the
       * input field.
       */
      newDate.setHours(0)
      newDate.setMinutes(0)
    } else {
      newDate.setHours(time.hour)
      newDate.setMinutes(time.minute)
    }

    setDate(newDate)
  }

  const formattedDate = React.useMemo(() => {
    if (!date) {
      return null
    }

    return formatDate(date, showTimePicker)
  }, [date, showTimePicker])

  const onApply = () => {
    setOpen(false)
    onChange?.(date)
  }

  return (
    <Primitives.Root open={open} onOpenChange={onOpenChange}>
      <Display
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        aria-required={props.required || props["aria-required"]}
        aria-invalid={props["aria-invalid"]}
        aria-label={props["aria-label"]}
        aria-labelledby={props["aria-labelledby"]}
      >
        {formattedDate}
      </Display>
      <Flyout disablePortal={disablePortal}>
        <div>
          <CalendarPrimitive
            mode="single"
            month={month}
            onMonthChange={setMonth}
            selected={date}
            onSelect={onDateChange}
            disabled={disabled}
            {...props}
          />
          {showTimePicker && (
            <div className="border-ui-border-base border-t p-3">
              <TimeInput
                aria-label={t("Time")}
                onChange={onTimeChange}
                isDisabled={!date}
                value={time}
                isRequired={props.required}
              />
            </div>
          )}
          <div className="border-ui-border-base flex items-center gap-x-2 border-t p-3">
            <Button
              variant="secondary"
              size="small"
              className="w-full"
              type="button"
              onClick={onCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary"
              size="small"
              className="w-full"
              type="button"
              onClick={onApply}
            >
              {t("Apply")}
            </Button>
          </div>
        </div>
      </Flyout>
    </Primitives.Root>
  )
}

interface RangeProps extends PickerProps {
  defaultValue?: DateRange
  value?: DateRange
  onChange?: (dateRange: DateRange | undefined) => void
}

const RangeDatePicker = ({
  /**
   * The date range selected by default.
   */
  defaultValue,
  /**
   * The selected date range.
   */
  value,
  /**
   * A function to handle the change in the selected date range.
   */
  onChange,
  showTimePicker,
  disabled,
  className,
  placeholder,
  disablePortal,
  ...props
}: RangeProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [range, setRange] = React.useState<DateRange | undefined>(
    value ?? defaultValue ?? undefined
  )
  const [month, setMonth] = React.useState<Date | undefined>(range?.from)

  const [startTime, setStartTime] = React.useState<TimeValue>(
    value?.from
      ? new Time(value.from.getHours(), value.from.getMinutes())
      : defaultValue?.from
      ? new Time(defaultValue.from.getHours(), defaultValue.from.getMinutes())
      : new Time(0, 0)
  )
  const [endTime, setEndTime] = React.useState<TimeValue>(
    value?.to
      ? new Time(value.to.getHours(), value.to.getMinutes())
      : defaultValue?.to
      ? new Time(defaultValue.to.getHours(), defaultValue.to.getMinutes())
      : new Time(0, 0)
  )

  const initialRange = React.useMemo(() => {
    return range
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * Update the range when the value changes.
   */
  React.useEffect(() => {
    setRange(value ?? defaultValue ?? undefined)
  }, [value, defaultValue])

  React.useEffect(() => {
    if (range) {
      setMonth(range.from)
    }
  }, [range])

  React.useEffect(() => {
    if (!open) {
      setMonth(range?.from)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const onRangeChange = (range: DateRange | undefined) => {
    const newRange = range

    if (showTimePicker) {
      if (newRange?.from && !startTime) {
        setStartTime(new Time(0, 0))
      }

      if (newRange?.to && !endTime) {
        setEndTime(new Time(0, 0))
      }

      if (newRange?.from && startTime) {
        newRange.from.setHours(startTime.hour)
        newRange.from.setMinutes(startTime.minute)
      }

      if (newRange?.to && endTime) {
        newRange.to.setHours(endTime.hour)
        newRange.to.setMinutes(endTime.minute)
      }
    }

    setRange(newRange)
  }

  const onCancel = () => {
    setRange(initialRange)
    setStartTime(
      initialRange?.from
        ? new Time(initialRange.from.getHours(), initialRange.from.getMinutes())
        : new Time(0, 0)
    )
    setEndTime(
      initialRange?.to
        ? new Time(initialRange.to.getHours(), initialRange.to.getMinutes())
        : new Time(0, 0)
    )
    setOpen(false)
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onCancel()
    }

    setOpen(open)
  }

  const onTimeChange = (time: TimeValue, pos: "start" | "end") => {
    switch (pos) {
      case "start":
        setStartTime(time)
        break
      case "end":
        setEndTime(time)
        break
    }

    if (!range) {
      return
    }

    if (pos === "start") {
      if (!range.from) {
        return
      }

      const newDate = new Date(range.from.getTime())

      if (!time) {
        newDate.setHours(0)
        newDate.setMinutes(0)
      } else {
        newDate.setHours(time.hour)
        newDate.setMinutes(time.minute)
      }

      setRange({
        ...range,
        from: newDate,
      })
    }

    if (pos === "end") {
      if (!range.to) {
        return
      }

      const newDate = new Date(range.to.getTime())

      if (!time) {
        newDate.setHours(0)
        newDate.setMinutes(0)
      } else {
        newDate.setHours(time.hour)
        newDate.setMinutes(time.minute)
      }

      setRange({
        ...range,
        to: newDate,
      })
    }
  }

  const displayRange = React.useMemo(() => {
    if (!range) {
      return null
    }

    return `${range.from ? formatDate(range.from, showTimePicker) : ""} - ${
      range.to ? formatDate(range.to, showTimePicker) : ""
    }`
  }, [range, showTimePicker])

  const onApply = () => {
    setOpen(false)
    onChange?.(range)
  }

  return (
    <Primitives.Root open={open} onOpenChange={onOpenChange}>
      <Display
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        aria-required={props.required || props["aria-required"]}
        aria-invalid={props["aria-invalid"]}
        aria-label={props["aria-label"]}
        aria-labelledby={props["aria-labelledby"]}
      >
        {displayRange}
      </Display>
      <Flyout disablePortal={disablePortal}>
        <div>
          <CalendarPrimitive
            mode="range"
            selected={range}
            onSelect={onRangeChange}
            month={month}
            onMonthChange={setMonth}
            numberOfMonths={2}
            disabled={disabled}
            classNames={{
              months: "flex flex-row divide-x divide-ui-border-base",
            }}
            {...props}
          />
          {showTimePicker && (
            <div className="border-ui-border-base flex items-center justify-evenly gap-x-3 border-t p-3">
              <div className="flex flex-1 items-center gap-x-2">
                <span className="text-ui-fg-subtle">
                  {t("Start")}:
                </span>
                <TimeInput
                  onChange={(v: TimeValue) => onTimeChange(v, "start")}
                  aria-label={t("Start date time")}
                  isDisabled={!range?.from}
                  isRequired={props.required}
                  placeholderValue={new Time(9, 12, 1, 3)}
                />
              </div>
              <MinusMiniIcon className="text-ui-fg-muted" />
              <div className="flex flex-1 items-center gap-x-2">
                <span className="text-ui-fg-subtle">
                  {t("End")}:
                </span>
                <TimeInput
                  value={endTime}
                  onChange={(v: TimeValue) => onTimeChange(v, "end")}
                  aria-label={t("End date time")}
                  isDisabled={!range?.to}
                  isRequired={props.required}
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between border-t p-3">
            <p className={clx("text-ui-fg-subtle txt-compact-small-plus")}>
              <span className="text-ui-fg-muted">
                {t("Range")}:
              </span>{" "}
              {displayRange}
            </p>
            <div className="flex items-center gap-x-2">
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={onCancel}
              >
                {t("Cancel")}
              </Button>
              <Button
                size="small"
                variant="primary"
                type="button"
                onClick={onApply}
              >
                {t("Apply")}
              </Button>
            </div>
          </div>
        </div>
      </Flyout>
    </Primitives.Root>
  )
}

/**
 * @interface
 *
 * @prop defaultValue - The date selected by default.
 * @prop value - The selected date.
 * @prop onChange - A function to handle the change in the selected date.
 */
type DatePickerProps = (
  | {
      mode?: "single"
      defaultValue?: Date
      value?: Date
      onChange?: (date: Date | undefined) => void
    }
  | {
      mode: "range"
      defaultValue?: DateRange
      value?: DateRange
      onChange?: (dateRange: DateRange | undefined) => void
    }
) &
  PickerProps

/**
 * This component is based on the [Calendar](https://docs.medusajs.com/ui/components/calendar)
 * component and [Radix UI Popover](https://www.radix-ui.com/primitives/docs/components/popover).
 */
const DatePicker = ({
  /**
   * The date picker's mode.
   */
  mode = "single",
  ...props
}: DatePickerProps) => {
  if (mode === "single") {
    return <SingleDatePicker {...(props as SingleProps)} />
  }

  return <RangeDatePicker {...(props as RangeProps)} />
}

export { DatePicker }
